import { FC, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import AuthWrapper from '../../common/AuthWrapper';
import CustomInput from '../../common/CustomInput';
import CustomPassInput from '../../common/CustomPassInput';
import CustomButton from '../../common/CustomButton';
import { useIsMobile } from '../../hooks/useWindowWidth';
import { useLoginUserMutation, useSignUpInitialMutation } from '../../store/services/authApi';
import { useAppDispatch } from '../../hooks/redux';
import { setFirstLoginStep } from '../../store/slices/authSlice';
import { clearInviteFlowFlag, setInviteFlowFlag } from '../../constants/inviteFlow';
import {
  captureReferralTokenFromLocation,
  clearReferralToken,
  getReferralToken,
} from '../../constants/referralToken';

import styles from './CreateAccount.module.scss';

const CreateAccount: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailFromUrl = queryParams.get('email') ?? '';

  const [signUpInitial, { isLoading: isRegistering }] = useSignUpInitialMutation();
  const [loginUser, { isLoading: isRequestingLoginCode }] = useLoginUserMutation();
  const [submitError, setSubmitError] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  useEffect(() => {
    setInviteFlowFlag(Boolean(emailFromUrl.trim()));
    captureReferralTokenFromLocation(location.pathname, location.search);
  }, [emailFromUrl, location.pathname, location.search]);

  const initialValues = {
    firstName: '',
    lastName: '',
    email: emailFromUrl,
    password: '',
  };

  const handleSubmit = async (
    values: { firstName: string; lastName: string; email: string; password: string },
    { setFieldError }: any
  ) => {
    setSubmitError(null);
    localStorage.setItem('createAccount', JSON.stringify(values));

    try {
      const emailLocal = values.email.trim();

      const referralToken = getReferralToken();

      await signUpInitial({
        email: emailLocal,
        password: values.password,
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        ...(referralToken ? { referral_token: referralToken } : {}),
      }).unwrap();

      clearReferralToken();

      localStorage.removeItem('userInfo');
      localStorage.removeItem('authToken');
      await loginUser({ email: emailLocal, password: values.password }).unwrap();

      dispatch(setFirstLoginStep(true));
      localStorage.setItem('userEmail', emailLocal);
      sessionStorage.setItem('pendingCodeVerification', 'sign-in');
      sessionStorage.setItem('postCodeVerificationRoute', 'onboarding');
      sessionStorage.setItem('pendingLoginPassword', values.password);
      clearInviteFlowFlag();
      navigate('/reset-password');
    } catch (error: any) {
      const errors = error?.data?.errors ?? [];

      const generalErrors: typeof submitError = [];

      errors.forEach((e: any) => {
        if (e.attr === 'first_name') {
          setFieldError('firstName', e.detail);
        } else if (e.attr === 'last_name') {
          setFieldError('lastName', e.detail);
        } else if (e.attr === 'email') {
          setFieldError('email', e.detail);
        } else if (e.attr === 'password') {
          setFieldError('password', e.detail);
        } else {
          generalErrors.push(e);
        }
      });
      setSubmitError(generalErrors.length ? generalErrors : null);
    }
  };

  const formikValidate = (values: { firstName: string; lastName: string; email: string; password: string }) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[^\s]{8,}$/;

    const errors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      password?: string;
    } = {};

    if (!values.firstName?.trim()) errors.firstName = 'First name is required';
    if (!values.lastName?.trim()) errors.lastName = 'Last name is required';
    if (!values.email?.trim()) errors.email = 'Email is required';
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) errors.email = 'Invalid email address';

    if (!values.password) errors.password = "Password can't be empty";
    else if (values.password.length < 8) errors.password = 'Password length should be at least 8 characters';
    else if (!passwordRegex.test(values.password))
      errors.password =
        'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    return errors;
  };

  return (
    <AuthWrapper btnName="Sign Up">
      <Box className={styles.contentWrapper}>
        <Box className={styles.loginContent}>
          <Typography
            variant={`${isMobile ? 'h6Bold' : 'h5Bold'}`}
            className={styles.title}
          >
            Create new account
          </Typography>
          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => {
              const fieldError = (field: 'firstName' | 'lastName' | 'email' | 'password') =>
                touched[field] && errors[field] ? errors[field]! : '';

              const hasEmptyRequired =
                !values.firstName?.trim() || !values.lastName?.trim() || !values.email?.trim() || !values.password;

              const hasValidationErrors = Object.values(errors).some(Boolean);

              return (
                <Form className={styles.loginForm}>
                  <Box className={styles.nameInputs}>
                    <CustomInput
                      inputLabel="First Name"
                      inputType="text"
                      inputName="firstName"
                      value={values.firstName}
                      error={fieldError('firstName')}
                      customClass={styles.customInput}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <CustomInput
                      inputLabel="Last Name"
                      inputType="text"
                      inputName="lastName"
                      value={values.lastName}
                      error={fieldError('lastName')}
                      customClass={styles.customInput}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Box>
                  <CustomInput
                    inputLabel="Email"
                    placeholder="email.example@gmail.com"
                    inputType="email"
                    inputName="email"
                    value={values.email}
                    error={fieldError('email')}
                    customClass={styles.customInput}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <CustomPassInput
                    label="Create Password"
                    value={values.password}
                    error={fieldError('password')}
                    customClass={styles.customInput}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <CustomButton
                    btnType="submit"
                    customClass={styles.customButton}
                    disabled={
                      isSubmitting || isRegistering || isRequestingLoginCode || hasEmptyRequired || hasValidationErrors
                    }
                  >
                    Create account
                  </CustomButton>

                  {submitError &&
                    submitError.map((error, index) => (
                      <Typography
                        key={index}
                        variant="h12"
                        sx={{ color: '#C8102E', mt: 1 }}
                      >
                        {error.detail}
                      </Typography>
                    ))}
                </Form>
              );
            }}
          </Formik>

          <Box className={styles.signInQuestion}>
            <Typography
              variant="h11"
              className={styles.signInQuestionText}
            >
              Already have an account?
            </Typography>
            <Link
              to="/"
              className={styles.signInLink}
            >
              <Typography variant="h11Semibold">Login</Typography>
            </Link>
          </Box>
        </Box>
        <Box className={styles.termsAndConditions}>
          <Typography
            variant="h11"
            sx={{ textAlign: 'center' }}
          >
            By signing up, you understand and agree with TravGenius'
          </Typography>
          <Typography variant="h11">
            <a
              href="/terms-privacy/TravGenius Terms and Conditions MAIN.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#222B3A' }}
            >
              Terms & Conditions
            </a>
            <span> and </span>
            <a
              href="/terms-privacy/TravGenius Privacy Policy (GDPR & CCPA) MAIN.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#222B3A' }}
            >
              Privacy Policy
            </a>
          </Typography>
        </Box>
      </Box>
    </AuthWrapper>
  );
};

export default CreateAccount;
